(() => {
  class BraceletBuilder {
    constructor(root) {
      this.root = root;
      this.styles = this.json(root.querySelector('[data-styles]'), []);
      this.style = this.styles[0] || null;
      this.baseLength = 14;
      this.length = 14;
      this.slots = Array(14).fill(null);
      this.history = [];
      this.selected = null;
      this.moving = null;
      this.activeIndex = 0;
      this.tourStep = 0;
      this.transparentImageCache = new Map();
      this.buildId = crypto.randomUUID ? crypto.randomUUID() : `bracelet-${Date.now()}`;
      this.els = {
        bracelet: root.querySelector('[data-bracelet]'),
        length: root.querySelector('[data-length]'),
        chosen: root.querySelector('[data-chosen]'),
        totals: [...root.querySelectorAll('[data-total]')],
        undo: root.querySelector('[data-undo]'),
        adds: [...root.querySelectorAll('[data-add]')],
        status: root.querySelector('[data-status]'),
        prompt: root.querySelector('[data-prompt]'),
        bar: root.querySelector('[data-checkout-bar]'),
        selectedStrip: root.querySelector('[data-selected-strip]'),
        selectedImage: root.querySelector('[data-selected-image]'),
        selectedKind: root.querySelector('[data-selected-kind]'),
        selectedName: root.querySelector('[data-selected-name]'),
        selectedInstruction: root.querySelector('[data-selected-instruction]'),
        removeSelected: root.querySelector('[data-remove-selected]'),
        currentLink: root.querySelector('[data-current-link]'),
        placeCurrent: root.querySelector('[data-place-current]'),
        placeLabel: root.querySelector('[data-place-label]'),
        placedCounts: [...root.querySelectorAll('[data-placed-count]')],
        tour: root.querySelector('[data-tour]'),
        tourCard: root.querySelector('[data-tour-card]'),
        tourSpotlight: root.querySelector('[data-tour-spotlight]'),
        tourCount: root.querySelector('[data-tour-count]'),
        tourMessage: root.querySelector('[data-tour-message]'),
        tourNext: root.querySelector('[data-tour-next]'),
        tourClose: root.querySelector('[data-tour-close]')
      };
      this.bind();
      this.observe();
      this.fitImages(root.querySelectorAll('.bb__fit-image'));
      this.render();
      this.ensurePanelLoaded(root.querySelector('[data-panel]:not([hidden])'));
      requestAnimationFrame(() => this.showTourStep());
    }

    json(node, fallback = null) {
      try { return JSON.parse(node.textContent); }
      catch (error) { console.error('Bracelet builder data error', error); return fallback; }
    }

    bind() {
      this.root.addEventListener('click', event => {
        let el = event.target.closest('[data-tour-next]');
        if (el) return this.advanceTour();
        el = event.target.closest('[data-tour-close]');
        if (el) return this.closeTour();
        el = event.target.closest('[data-remove-selected]');
        if (el) return this.removeSelectedLink();
        el = event.target.closest('[data-clear-selection]');
        if (el) return this.clearSelection();
        el = event.target.closest('[data-prev-link]');
        if (el) return this.moveLink(-1);
        el = event.target.closest('[data-next-link]');
        if (el) return this.moveLink(1);
        el = event.target.closest('[data-place-current]');
        if (el) return this.placeCurrent();
        el = event.target.closest('[data-style]');
        if (el) return this.changeStyle(el.dataset.style);
        el = event.target.closest('[data-tab]');
        if (el) return this.changeTab(el);
        el = event.target.closest('[data-pick]');
        if (el) return this.selectCharm(el.closest('[data-card]'));
        el = event.target.closest('[data-index]');
        if (el) return this.linkClick(+el.dataset.index);
        el = event.target.closest('[data-remove]');
        if (el) return this.remove(+el.dataset.remove);
      });
      this.els.length.addEventListener('change', () => this.changeLength(+this.els.length.value));
      this.els.undo.addEventListener('click', () => this.undo());
      this.root.querySelector('[data-reset]').addEventListener('click', () => this.reset());
      this.els.adds.forEach(button => button.addEventListener('click', () => this.addToCart()));
      this.root.querySelector('[data-search]').addEventListener('input', event => this.search(event.target.value));
      this.root.addEventListener('change', event => {
        if (!event.target.matches('[data-charm-variant]')) return;
        const card = event.target.closest('[data-card]');
        this.syncVariant(card, event.target);
        if (card.querySelector('[data-pick]').classList.contains('is-selected')) this.selectCharm(card);
        else this.updateUsedProducts();
      });
      window.addEventListener('resize', () => this.positionTour());
      window.addEventListener('scroll', () => this.positionTour(), { passive: true });
      document.addEventListener('keydown', event => {
        if (!this.els.tour || this.els.tour.hidden) return;
        if (event.key === 'Escape') this.closeTour();
        else if (event.key === 'Tab') {
          const focusable = [this.els.tourClose, this.els.tourNext].filter(Boolean);
          const current = focusable.indexOf(document.activeElement);
          const next = event.shiftKey
            ? (current <= 0 ? focusable.length - 1 : current - 1)
            : (current + 1) % focusable.length;
          event.preventDefault();
          focusable[next]?.focus();
        }
      });
    }

    tourTarget() {
      if (this.tourStep === 0) return this.root.querySelector('[data-tour-finish]');
      if (this.tourStep === 1) return this.root.querySelector('[data-tour-length]');
      return this.root.querySelector('[data-panel]:not([hidden]) [data-pick]:not(:disabled)')
        || this.root.querySelector('.bb__library');
    }

    showTourStep() {
      if (!this.els.tour || this.els.tour.hidden) return;
      const messages = [
        'Select your Base Bracelet Colour.',
        'Select your Bracelet length.',
        'Select your first link.'
      ];
      this.els.tourCount.textContent = `Step ${this.tourStep + 1} of 3`;
      this.els.tourMessage.textContent = messages[this.tourStep];
      const target = this.tourTarget();
      if (this.tourStep === 2) {
        target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => this.positionTour(), 450);
      } else this.positionTour();
      this.els.tourNext?.focus();
    }

    advanceTour() {
      if (this.tourStep >= 2) return this.completeTour();
      this.tourStep += 1;
      this.showTourStep();
    }

    completeTour() {
      if (!this.els.tour) return;
      this.els.tour.hidden = true;
      const finish = this.root.querySelector('[data-tour-finish]');
      finish?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => this.root.querySelector('[data-style]:not(:disabled)')?.focus(), 450);
    }

    closeTour() {
      if (!this.els.tour) return;
      this.els.tour.hidden = true;
      this.tourTarget()?.focus?.();
    }

    positionTour() {
      if (!this.els.tour || this.els.tour.hidden || !this.els.tourCard || !this.els.tourSpotlight) return;
      const target = this.tourTarget();
      if (!target) return;
      const rect = target.getBoundingClientRect();
      const padding = 9;
      const spotlight = this.els.tourSpotlight;
      spotlight.style.left = `${Math.max(4, rect.left - padding)}px`;
      spotlight.style.top = `${Math.max(4, rect.top - padding)}px`;
      spotlight.style.width = `${Math.min(window.innerWidth - 8, rect.width + padding * 2)}px`;
      spotlight.style.height = `${Math.min(window.innerHeight - 8, rect.height + padding * 2)}px`;

      const card = this.els.tourCard;
      const gap = 20;
      const cardWidth = Math.min(360, window.innerWidth - 24);
      card.style.width = `${cardWidth}px`;
      const cardHeight = card.offsetHeight;
      const placeBelow = rect.bottom + gap + cardHeight <= window.innerHeight - 12;
      const top = placeBelow
        ? rect.bottom + gap
        : Math.max(12, rect.top - gap - cardHeight);
      const left = Math.max(12, Math.min(window.innerWidth - cardWidth - 12, rect.left + rect.width / 2 - cardWidth / 2));
      card.style.left = `${left}px`;
      card.style.top = `${top}px`;
      card.dataset.placement = placeBelow ? 'below' : 'above';
      card.style.setProperty('--bb-tour-arrow-x', `${Math.max(22, Math.min(cardWidth - 22, rect.left + rect.width / 2 - left))}px`);
    }

    observe() {
      if (!this.els.bar) return;
      if (!('IntersectionObserver' in window)) {
        this.els.bar.classList.add('is-visible');
        return;
      }
      this.visibilityObserver = new IntersectionObserver(
        entries => this.els.bar.classList.toggle('is-visible', Boolean(entries[0]?.isIntersecting)),
        { threshold: 0.01 }
      );
      this.visibilityObserver.observe(this.root);
    }

    changeStyle(id) {
      const next = this.styles.find(style => style.id === id);
      if (!next) return;
      this.style = next;
      this.root.querySelectorAll('[data-style]').forEach(button => {
        const active = button.dataset.style === id;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-checked', active);
      });
      this.render();
    }

    changeLength(length) {
      if (![14, 15, 17, 19, 21, 23].includes(length) || length === this.baseLength) return;
      this.save();
      this.moving = null;
      const difference = length - this.baseLength;
      if (difference > 0) this.slots.push(...Array(difference).fill(null));
      else this.removePlainSpaces(-difference);
      this.baseLength = length;
      this.length = this.slots.length;
      this.normalizeSlots();
      this.render();
    }

    isContinuation(slot) {
      return Boolean(slot?.isContinuation);
    }

    normalizeSlots() {
      const normalized = Array(this.length).fill(null);
      this.slots.forEach((slot, index) => {
        if (!slot || this.isContinuation(slot) || normalized[index]) return;
        if (slot.isDoubleLink) {
          if (index + 1 >= this.length || normalized[index + 1]) return;
          normalized[index] = { ...slot };
          normalized[index + 1] = { isContinuation: true, parentIndex: index };
        } else normalized[index] = { ...slot };
      });
      this.slots = normalized;
    }

    canPlace(index, charm) {
      if (!charm || index <= 0 || index >= this.length || this.slots[index]) return false;
      return !charm.isDoubleLink || (index + 1 < this.length && !this.slots[index + 1]);
    }

    canMove(sourceIndex, targetIndex) {
      const charm = this.slots[sourceIndex];
      return Boolean(charm && !this.isContinuation(charm) && sourceIndex > 0 && targetIndex !== sourceIndex && this.canPlace(targetIndex, charm));
    }

    placementIndex(index, charm, sourceIndex = null) {
      const canUse = candidate => sourceIndex === null
        ? this.canPlace(candidate, charm)
        : this.canMove(sourceIndex, candidate);
      if (canUse(index)) return index;
      if (charm?.isDoubleLink && index > 1 && canUse(index - 1)) return index - 1;
      return -1;
    }

    render() {
      this.activeIndex = Math.max(0, Math.min(this.activeIndex, this.length - 1));
      const plain = this.style?.linkImage || '';
      this.els.bracelet.innerHTML = this.slots.map((charm, index) => {
        if (this.isContinuation(charm)) return '';
        const image = charm?.image || (index === 0 ? (this.style?.firstLinkImage || plain) : plain);
        const isHangingCharm = Boolean(charm?.isHangingCharm);
        const isDoubleLink = Boolean(charm?.isDoubleLink);
        const imageClass = charm ? (isHangingCharm ? ' is-hanging-charm' : '') : ' is-plain-link';
        const locked = index === 0;
        const label = locked
          ? 'Fixed first bracelet link. This link cannot be changed.'
          : charm
            ? `${charm.title}, ${isDoubleLink ? `links ${index + 1} and ${index + 2}` : `link ${index + 1}`}. Select to move or remove.`
            : `Standard link ${index + 1}. Select to place a chosen link.`;
        const number = isDoubleLink ? `${index + 1}–${index + 2}` : index + 1;
        return `<button type="button" class="bb__link${locked ? ' is-locked' : ''}${isHangingCharm ? ' is-hanging-charm' : ''}${isDoubleLink ? ' is-double-link' : ''}${this.moving?.index === index ? ' is-moving' : ''}${index === this.activeIndex ? ' is-current' : ''}" data-index="${index}" role="listitem" aria-label="${this.escape(label)}">${image ? `<img class="${imageClass.trim()}" src="${this.escape(image)}" alt="" crossorigin="anonymous" data-auto-fit>` : ''}<span>${number}</span></button>`;
      }).join('');
      this.fitImages(this.els.bracelet.querySelectorAll('[data-auto-fit]'));
      this.summary();
      this.renderSelection();
      this.updateCurrentLink();
    }

    charm(card) {
      const data = this.json(card.querySelector('[data-product-json]'));
      const select = card.querySelector('[data-charm-variant]');
      if (select) this.syncVariant(card, select, data);
      return data?.available ? { ...data } : null;
    }

    syncVariant(card, select, data = this.json(card.querySelector('[data-product-json]'))) {
      const option = select.options[select.selectedIndex];
      Object.assign(data, {
        id: +option.value,
        variantTitle: option.textContent.split(' — ')[0].trim(),
        price: +option.dataset.price,
        compareAtPrice: +option.dataset.compareAt || 0,
        sku: option.dataset.sku || '',
        available: !option.disabled
      });
      card.querySelector('[data-product-json]').textContent = JSON.stringify(data);
      this.updatePrice(card, data);
    }

    updatePrice(card, data) {
      const current = card.querySelector('[data-price-current]');
      const compare = card.querySelector('[data-price-compare]');
      const onSale = +data.compareAtPrice > +data.price;
      if (current) {
        current.textContent = this.money(+data.price || 0);
        current.classList.toggle('is-sale', onSale);
      }
      if (compare) {
        compare.hidden = !onSale;
        compare.textContent = onSale ? this.money(+data.compareAtPrice) : '';
      }
    }

    selectCharm(card) {
      const charm = this.charm(card);
      if (!charm) return;
      this.clearMoving();
      this.selected = charm;
      const firstAvailable = this.slots.findIndex((slot, index) => !slot && this.canPlace(index, charm));
      if (firstAvailable >= 0) this.activeIndex = firstAvailable;
      this.root.querySelectorAll('[data-pick]').forEach(button => button.classList.remove('is-selected'));
      card.querySelector('[data-pick]').classList.add('is-selected');
      this.els.prompt.textContent = `${charm.title} selected — now choose a bracelet link.`;
      this.els.prompt.textContent = firstAvailable >= 0
        ? `${charm.title} selected — choose one of the available bracelet positions.`
        : `There is no available ${charm.isDoubleLink ? 'two-link space' : 'space'} for ${charm.title}.`;
      this.renderSelection();
      this.updateCurrentLink();
      this.els.bracelet.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    linkClick(index) {
      if (index === 0) {
        this.els.prompt.textContent = 'The first bracelet link is fixed and cannot be changed.';
        return;
      }
      if (this.selected) {
        const targetIndex = this.placementIndex(index, this.selected);
        if (targetIndex < 0) {
          this.els.prompt.textContent = this.selected.isDoubleLink
            ? 'A double link needs two consecutive empty spaces.'
            : 'This bracelet position is already occupied.';
          return;
        }
        const placedCharm = this.selected;
        this.place(targetIndex, this.selected, true);
        this.selected = null;
        this.root.querySelectorAll('[data-pick]').forEach(button => button.classList.remove('is-selected'));
        this.els.prompt.textContent = `Link ${targetIndex + 1} updated.`;
        this.els.prompt.textContent = placedCharm.isDoubleLink
          ? `Links ${targetIndex + 1} and ${targetIndex + 2} updated.`
          : `Link ${targetIndex + 1} updated.`;
        this.renderSelection();
        this.updateCurrentLink();
      } else if (this.moving) {
        if (index === this.moving.index) return this.clearSelection();
        const targetIndex = this.placementIndex(index, this.moving.charm, this.moving.index);
        if (targetIndex < 0) {
          this.els.prompt.textContent = this.moving.charm.isDoubleLink
            ? 'Move a double link to two consecutive empty spaces.'
            : 'Choose an empty bracelet position.';
          return;
        }
        this.movePlacedLink(this.moving.index, targetIndex);
      } else if (this.slots[index]) this.selectPlacedLink(index);
      else this.els.prompt.textContent = 'Choose a link below, then select this position.';
    }

    place(index, charm, expand = false) {
      if (!this.canPlace(index, charm)) return false;
      this.save();
      this.slots[index] = { ...charm };
      if (charm.isDoubleLink) this.slots[index + 1] = { isContinuation: true, parentIndex: index };
      if (expand) {
        const spaces = charm.isDoubleLink ? 2 : 1;
        this.slots.push(...Array(spaces).fill(null));
        this.length = this.slots.length;
      }
      this.render();
      return true;
    }

    selectPlacedLink(index) {
      const charm = this.slots[index];
      if (!charm || this.isContinuation(charm) || index === 0) return;
      this.selected = null;
      this.root.querySelectorAll('[data-pick]').forEach(button => button.classList.remove('is-selected'));
      this.moving = { index, charm: { ...charm } };
      this.activeIndex = index;
      this.els.prompt.textContent = charm.isDoubleLink
        ? 'Double link selected. Choose two consecutive empty spaces, or remove it.'
        : 'Link selected. Choose an empty position, or remove it.';
      this.render();
    }

    clearMoving() {
      this.moving = null;
    }

    removeSelectedLink() {
      if (!this.moving) return;
      const { index, charm } = this.moving;
      this.remove(index);
      this.els.prompt.textContent = `${charm.title} removed from the bracelet.`;
    }

    movePlacedLink(sourceIndex, targetIndex) {
      if (!this.canMove(sourceIndex, targetIndex)) return false;
      const charm = { ...this.slots[sourceIndex] };
      this.save();
      this.slots[sourceIndex] = null;
      if (charm.isDoubleLink && this.isContinuation(this.slots[sourceIndex + 1])) this.slots[sourceIndex + 1] = null;
      this.slots[targetIndex] = charm;
      if (charm.isDoubleLink) this.slots[targetIndex + 1] = { isContinuation: true, parentIndex: targetIndex };
      this.moving = null;
      this.activeIndex = targetIndex;
      this.els.prompt.textContent = charm.isDoubleLink
        ? `Moved to links ${targetIndex + 1} and ${targetIndex + 2}.`
        : `Moved to link ${targetIndex + 1}.`;
      this.render();
      return true;
    }

    remove(index) {
      let slot = this.slots[index];
      if (!slot) return;
      if (this.isContinuation(slot)) {
        index = slot.parentIndex;
        slot = this.slots[index];
      }
      this.save();
      this.slots[index] = null;
      if (slot?.isDoubleLink && this.isContinuation(this.slots[index + 1])) this.slots[index + 1] = null;
      this.removePlainSpaces(slot?.isDoubleLink ? 2 : 1);
      this.length = this.slots.length;
      this.normalizeSlots();
      this.moving = null;
      this.render();
    }

    removePlainSpaces(count) {
      for (let index = this.slots.length - 1; index > 0 && count > 0; index -= 1) {
        if (this.slots[index] !== null) continue;
        this.slots.splice(index, 1);
        count -= 1;
      }
    }

    save() {
      this.history.push({ baseLength: this.baseLength, length: this.length, slots: this.slots.map(slot => slot ? { ...slot } : null) });
      if (this.history.length > 30) this.history.shift();
      this.els.undo.disabled = false;
    }

    undo() {
      const state = this.history.pop();
      if (!state) return;
      this.length = state.length;
      this.baseLength = state.baseLength ?? state.length;
      this.els.length.value = this.baseLength;
      this.slots = state.slots;
      this.els.undo.disabled = !this.history.length;
      this.render();
    }

    reset() {
      if (!this.slots.some(Boolean)) return;
      this.save();
      this.length = this.baseLength;
      this.slots = Array(this.baseLength).fill(null);
      this.selected = null;
      this.moving = null;
      this.root.querySelectorAll('[data-pick]').forEach(button => button.classList.remove('is-selected'));
      this.els.prompt.textContent = 'Bracelet reset.';
      this.render();
    }

    clearSelection() {
      this.selected = null;
      this.moving = null;
      this.root.querySelectorAll('[data-pick]').forEach(button => button.classList.remove('is-selected'));
      this.els.prompt.textContent = 'Selection cleared.';
      this.renderSelection();
      this.updateCurrentLink();
    }

    renderSelection() {
      if (!this.els.selectedStrip) return;
      const activeSelection = this.selected || this.moving?.charm;
      this.els.selectedStrip.hidden = !activeSelection;
      this.els.bracelet.classList.toggle('is-placing', Boolean(activeSelection));
      this.updateAvailableLinks();
      if (!activeSelection) {
        if (this.els.removeSelected) this.els.removeSelected.hidden = true;
        this.els.selectedImage.removeAttribute('src');
        this.els.selectedImage.alt = '';
        this.els.selectedName.textContent = '';
        return;
      }
      this.els.selectedImage.src = activeSelection.image || '';
      this.els.selectedImage.alt = activeSelection.title || 'Selected link';
      this.els.selectedName.textContent = activeSelection.title;
      if (this.els.removeSelected) this.els.removeSelected.hidden = !this.moving;
      if (this.els.selectedKind) this.els.selectedKind.textContent = this.moving ? 'Moving bracelet link' : 'Selected link';
      if (this.els.selectedInstruction) this.els.selectedInstruction.textContent = this.moving ? 'Choose an empty position or remove it' : 'Choose a bracelet position';
    }

    updateAvailableLinks(charm = this.selected || this.moving?.charm, sourceIndex = this.moving?.index ?? null) {
      this.els.bracelet.querySelectorAll('[data-index]').forEach(link => {
        const index = +link.dataset.index;
        const available = this.placementIndex(index, charm, sourceIndex) >= 0;
        link.classList.toggle('is-available', Boolean(charm) && available);
      });
    }

    moveLink(direction) {
      let links = [...this.els.bracelet.querySelectorAll('[data-index]')];
      if (this.selected) links = links.filter(link => this.placementIndex(+link.dataset.index, this.selected) >= 0);
      else if (this.moving) links = links.filter(link => this.placementIndex(+link.dataset.index, this.moving.charm, this.moving.index) >= 0);
      if (!links.length) return;
      let position = links.findIndex(link => +link.dataset.index === this.activeIndex);
      if (position < 0) position = direction > 0 ? -1 : 0;
      position = (position + direction + links.length) % links.length;
      this.activeIndex = +links[position].dataset.index;
      this.updateCurrentLink(true);
    }

    updateCurrentLink(scroll = false) {
      this.els.bracelet.querySelectorAll('[data-index]').forEach(link => {
        const current = +link.dataset.index === this.activeIndex;
        link.classList.toggle('is-current', current);
        if (current) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      if (this.els.currentLink) this.els.currentLink.textContent = `Link ${this.activeIndex + 1} of ${this.length}`;
      if (this.els.placeLabel) this.els.placeLabel.textContent = this.moving ? 'Move selected link' : (this.selected ? 'Place selected link' : 'Choose a link');
      if (this.els.placeCurrent) this.els.placeCurrent.disabled = this.moving
        ? this.placementIndex(this.activeIndex, this.moving.charm, this.moving.index) < 0
        : (!this.selected || this.placementIndex(this.activeIndex, this.selected) < 0);
      if (scroll) {
        const current = this.els.bracelet.querySelector(`[data-index="${this.activeIndex}"]`);
        current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }

    placeCurrent() {
      if (!this.selected && !this.moving) {
        this.els.prompt.textContent = 'Choose a link before placing it.';
        return;
      }
      this.linkClick(this.activeIndex);
    }

    variant() {
      const variants = this.style?.variants || [];
      const needle = String(this.baseLength);
      const skuMatch = variants.find(variant =>
        new RegExp(`(?:^|[-_\\s])${needle}$`).test(String(variant.sku || '').trim())
      );
      if (skuMatch) return skuMatch;
      return variants.find(variant => [variant.title, ...(variant.options || [])].some(value => {
        value = String(value).toLowerCase();
        return value === needle || value === `${needle} links` || new RegExp(`(^|\\D)${needle}(\\D|$)`).test(value);
      })) || variants[[14, 15, 17, 19, 21, 23].indexOf(this.baseLength)] || null;
    }

    summary() {
      const base = this.variant();
      const charms = this.slots.map((charm, index) => ({ charm, index })).filter(item => item.charm && !this.isContinuation(item.charm));
      this.root.querySelector('[data-summary-style]').textContent = this.style?.label || 'Select a finish';
      this.root.querySelector('[data-summary-length]').textContent = `${this.length} links`;
      const baseTitle = `${this.style?.label || ''} base bracelet`.trim();
      const baseImage = this.style?.linkImage || this.style?.firstLinkImage || '';
      const baseItem = `<li class="bb__chosen bb__chosen--base">${baseImage ? `<img src="${this.escape(baseImage)}" alt="">` : '<i></i>'}<span><b>${this.escape(baseTitle)}</b><small>${this.baseLength}-link base${base?.sku ? ` &middot; ${this.escape(base.sku)}` : ''}</small><strong class="bb__chosen-price">${this.money(base?.price || 0)}</strong></span></li>`;
      const linkItems = charms.map(({ charm, index }) =>
        `<li class="bb__chosen">${charm.image ? `<img src="${this.escape(charm.image)}" alt="">` : '<i></i>'}<span><b>${this.escape(charm.title)}</b><small>Link ${index + 1}${charm.sku ? ` &middot; ${this.escape(charm.sku)}` : ''}</small><strong class="bb__chosen-price">${this.money(charm.price || 0)}</strong></span><button type="button" data-remove="${index}" aria-label="Remove ${this.escape(charm.title)}">&times;</button></li>`
      ).join('');
      this.els.chosen.innerHTML = baseItem + linkItems;
      const cents = +(base?.price || 0) + charms.reduce((sum, item) => sum + (+item.charm.price || 0), 0);
      const formatted = this.money(cents);
      this.els.totals.forEach(total => { total.textContent = formatted; });
      const occupiedSpaces = charms.reduce((total, item) => total + (item.charm.isDoubleLink ? 2 : 1), 0);
      const placedLabel = `${occupiedSpaces} ${occupiedSpaces === 1 ? 'space' : 'spaces'} filled`;
      this.els.placedCounts.forEach(count => { count.textContent = placedLabel; });
      this.els.adds.forEach(button => { button.disabled = !base?.available; });
      this.updateUsedProducts();
    }

    updateUsedProducts() {
      const used = this.slots.reduce((counts, charm) => {
        if (charm && !this.isContinuation(charm)) counts.set(+charm.id, (counts.get(+charm.id) || 0) + 1);
        return counts;
      }, new Map());
      this.root.querySelectorAll('[data-card]').forEach(card => {
        const data = this.json(card.querySelector('[data-product-json]'));
        const button = card.querySelector('[data-pick]');
        const count = used.get(+data?.id) || 0;
        if (!button.dataset.baseLabel) button.dataset.baseLabel = button.getAttribute('aria-label') || 'Select charm';
        button.classList.toggle('is-used', count > 0);
        button.setAttribute('aria-label', count ? `${button.dataset.baseLabel}. Used ${count} ${count === 1 ? 'time' : 'times'}.` : button.dataset.baseLabel);
      });
    }

    fitImages(images) {
      images.forEach(img => {
        if (img.dataset.autoFitState) return;
        const fit = () => this.fitImage(img);
        if (img.complete && img.naturalWidth) fit();
        else img.addEventListener('load', fit, { once: true });
      });
    }

    fitImage(img) {
      if (img.dataset.autoFitState) return;
      img.dataset.autoFitState = 'working';
      const isPlainLink = img.classList.contains('is-plain-link');
      if (isPlainLink) {
        // Base-link sources share the same geometry, so their colour must not
        // affect sizing. Apply this before canvas analysis, which can fail to
        // distinguish the pale Gold/Rose Gold artwork from its background.
        img.style.setProperty('--bb-fit-scale', '1.100');
        img.style.setProperty('--bb-fit-x', '0%');
        img.style.setProperty('--bb-fit-y', '0%');
        img.dataset.autoFitState = 'done';
        return;
      }
      try {
        const size = 128;
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = size;
        canvas.height = size;
        context.drawImage(img, 0, 0, size, size);
        const pixels = context.getImageData(0, 0, size, size).data;
        const cornerSamples = [];
        const sampleSize = 7;
        [[0, 0], [size - sampleSize, 0], [0, size - sampleSize], [size - sampleSize, size - sampleSize]].forEach(([startX, startY]) => {
          for (let y = startY; y < startY + sampleSize; y += 1) {
            for (let x = startX; x < startX + sampleSize; x += 1) {
              const pixel = (y * size + x) * 4;
              cornerSamples.push([pixels[pixel], pixels[pixel + 1], pixels[pixel + 2], pixels[pixel + 3]]);
            }
          }
        });
        const background = cornerSamples.reduce(
          (sum, sample) => sum.map((value, index) => value + sample[index] / cornerSamples.length),
          [0, 0, 0, 0]
        );
        const backgroundSpread = cornerSamples.reduce((sum, sample) => sum + Math.hypot(
          sample[0] - background[0],
          sample[1] - background[1],
          sample[2] - background[2]
        ), 0) / cornerSamples.length;
        const distanceThreshold = Math.max(30, backgroundSpread * 3.2);
        const columns = Array(size).fill(0);
        const rows = Array(size).fill(0);
        let foregroundPixels = 0;
        for (let y = 0; y < size; y += 1) {
          for (let x = 0; x < size; x += 1) {
            const pixel = (y * size + x) * 4;
            const alpha = pixels[pixel + 3];
            const distance = Math.hypot(
              pixels[pixel] - background[0],
              pixels[pixel + 1] - background[1],
              pixels[pixel + 2] - background[2]
            );
            if (alpha > 30 && (background[3] < 48 || distance > distanceThreshold)) {
              columns[x] += 1;
              rows[y] += 1;
              foregroundPixels += 1;
            }
          }
        }
        if (foregroundPixels > 20) {
          const trim = Math.max(1, foregroundPixels * 0.006);
          const lowerBound = counts => {
            let seen = 0;
            for (let index = 0; index < counts.length; index += 1) {
              seen += counts[index];
              if (seen >= trim) return index;
            }
            return 0;
          };
          const upperBound = counts => {
            let seen = 0;
            for (let index = counts.length - 1; index >= 0; index -= 1) {
              seen += counts[index];
              if (seen >= trim) return index;
            }
            return counts.length - 1;
          };
          const left = lowerBound(columns);
          const right = upperBound(columns);
          const top = lowerBound(rows);
          const bottom = upperBound(rows);
          const width = (right - left + 1) / size;
          const height = (bottom - top + 1) / size;
          const centerX = (left + right + 1) / (2 * size);
          const centerY = (top + bottom + 1) / (2 * size);
          const aspect = img.naturalWidth / img.naturalHeight;
          const visualWidth = aspect >= 1 ? width : width * aspect;
          const visualHeight = aspect >= 1 ? height / aspect : height;
          const visualCenterX = aspect >= 1 ? centerX : 0.5 + (centerX - 0.5) * aspect;
          const visualCenterY = aspect >= 1 ? 0.5 + (centerY - 0.5) / aspect : centerY;
          const visualTop = aspect >= 1 ? 0.5 + ((top / size) - 0.5) / aspect : top / size;
          const baseScale = Math.max(1.15, Math.min(3.25, 0.97 / Math.max(visualWidth, visualHeight)));
          const isBraceletLink = Boolean(img.closest('.bb__link'));
          const isHangingCharm = img.classList.contains('is-hanging-charm') || Boolean(img.closest('.is-hanging-charm'));
          const isDoubleLink = Boolean(img.closest('.is-double-link'));
          const hangingTargetWidth = 1.24;
          const hangingTargetTop = -0.025;
          const coverScale = 1.18 / Math.max(0.01, Math.min(visualWidth, visualHeight));
          let scale = isBraceletLink ? Math.min(4, Math.max(baseScale * 1.12, coverScale)) : baseScale;
          if (isHangingCharm && isBraceletLink) scale = Math.min(4.5, Math.max(1, hangingTargetWidth / Math.max(0.01, visualWidth)));
          else if (isHangingCharm) scale *= 0.82;
          else if (isDoubleLink && isBraceletLink) scale *= 0.45;
          else if (isBraceletLink) scale *= 0.94;
          img.style.setProperty('--bb-fit-scale', scale.toFixed(3));
          const fitX = (0.5 - visualCenterX) * (isHangingCharm && isBraceletLink ? scale : 1);
          img.style.setProperty('--bb-fit-x', `${(fitX * 100).toFixed(2)}%`);
          const fitY = isHangingCharm && isBraceletLink
            ? hangingTargetTop - (0.5 + scale * (visualTop - 0.5))
            : 0.5 - visualCenterY;
          img.style.setProperty('--bb-fit-y', `${(fitY * 100).toFixed(2)}%`);
          if (isHangingCharm && isBraceletLink) {
            const verticalCropMargin = 2;
            const horizontalCropBoost = 3;
            img.style.setProperty('--bb-crop-top', `${Math.max(0, top / size * 100 - verticalCropMargin).toFixed(2)}%`);
            img.style.setProperty('--bb-crop-right', `${Math.max(0, (1 - (right + 1) / size) * 100 + horizontalCropBoost).toFixed(2)}%`);
            img.style.setProperty('--bb-crop-bottom', `${Math.max(0, (1 - (bottom + 1) / size) * 100 - verticalCropMargin).toFixed(2)}%`);
            img.style.setProperty('--bb-crop-left', `${Math.max(0, left / size * 100 + horizontalCropBoost).toFixed(2)}%`);
          }
          if (isBraceletLink) this.makeWhiteTransparent(img);
        }
        img.dataset.autoFitState = 'done';
      } catch (error) {
        img.dataset.autoFitState = 'fallback';
      }
    }

    makeWhiteTransparent(img) {
      const source = img.currentSrc || img.src;
      if (!source || source.startsWith('data:')) return;
      const cached = this.transparentImageCache.get(source);
      if (cached) {
        img.src = cached;
        return;
      }
      try {
        const maximumSize = 700;
        const ratio = Math.min(1, maximumSize / Math.max(img.naturalWidth, img.naturalHeight));
        const width = Math.max(1, Math.round(img.naturalWidth * ratio));
        const height = Math.max(1, Math.round(img.naturalHeight * ratio));
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d', { willReadFrequently: true });
        canvas.width = width;
        canvas.height = height;
        context.drawImage(img, 0, 0, width, height);
        const imageData = context.getImageData(0, 0, width, height);
        const pixels = imageData.data;
        const visited = new Uint8Array(width * height);
        const queue = new Int32Array(width * height);
        let head = 0;
        let tail = 0;
        const isBackground = index => {
          const pixel = index * 4;
          const alpha = pixels[pixel + 3];
          if (alpha < 20) return true;
          const red = pixels[pixel];
          const green = pixels[pixel + 1];
          const blue = pixels[pixel + 2];
          return Math.min(red, green, blue) >= 225 && Math.max(red, green, blue) - Math.min(red, green, blue) <= 28;
        };
        const enqueue = index => {
          if (index < 0 || index >= visited.length || visited[index] || !isBackground(index)) return;
          visited[index] = 1;
          queue[tail++] = index;
        };
        for (let x = 0; x < width; x += 1) {
          enqueue(x);
          enqueue((height - 1) * width + x);
        }
        for (let y = 1; y < height - 1; y += 1) {
          enqueue(y * width);
          enqueue(y * width + width - 1);
        }
        while (head < tail) {
          const index = queue[head++];
          const pixel = index * 4;
          if (pixels[pixel + 3] >= 20) {
            const luminance = (pixels[pixel] + pixels[pixel + 1] + pixels[pixel + 2]) / 3;
            const edgeOpacity = Math.max(0, Math.min(1, (248 - luminance) / 18));
            pixels[pixel + 3] = Math.round(pixels[pixel + 3] * edgeOpacity);
          }
          const x = index % width;
          if (x > 0) enqueue(index - 1);
          if (x < width - 1) enqueue(index + 1);
          if (index >= width) enqueue(index - width);
          if (index < width * (height - 1)) enqueue(index + width);
        }
        context.putImageData(imageData, 0, 0);
        const transparentSource = canvas.toDataURL('image/png');
        this.transparentImageCache.set(source, transparentSource);
        img.src = transparentSource;
      } catch (error) {
        this.transparentImageCache.set(source, source);
      }
    }

    money(cents) {
      return `R${(Number(cents || 0) / 100).toFixed(2)}`;
    }

    changeTab(tab) {
      const id = tab.dataset.tab;
      this.root.querySelectorAll('[data-tab]').forEach(button => {
        const active = button === tab;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-selected', active);
      });
      let activePanel = null;
      this.root.querySelectorAll('[data-panel]').forEach(panel => {
        panel.hidden = panel.dataset.panel !== id;
        if (!panel.hidden) activePanel = panel;
      });
      this.ensurePanelLoaded(activePanel);
    }

    cleanTitle(value) {
      return String(value || '')
        .replace(/Nomination Composable Double Link/gi, '')
        .replace(/Nomination Composable Link/gi, '')
        .replace(/Nomination Charm/gi, '')
        .replace(/Nomination/gi, '')
        .replace(/Composable Classic Link/gi, '')
        .replace(/Classic Link/gi, '')
        .replace(/Composable Classic Charm/gi, '')
        .replace(/Composable Charm/gi, '')
        .replace(/Stainless Steel Charm/gi, '')
        .replace(/Composable Classic/gi, '')
        .replace(/18K Gold\s*&\s*Enamel/gi, '')
        .replace(/9K Rose Gold\s*&\s*Enamel/gi, '')
        .replace(/Etched\s*,?\s*18K Gold/gi, '')
        .replace(/Silver\s*&\s*Enamel/gi, '')
        .replace(/&\s*Glitter Enamel/gi, '')
        .replace(/Cubic Zirconia/gi, '')
        .replace(/\bCZ\b/gi, '')
        .replace(/Resin and Stone/gi, '')
        .replace(/in and Stone/gi, '')
        .replace(/9K Rose Gold/gi, '')
        .replace(/18K Gold/gi, '')
        .replace(/Silver/gi, '')
        .replace(/,/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    priceToCents(value) {
      if (typeof value === 'number') return Number.isInteger(value) && value > 1000 ? value : Math.round(value * 100);
      const amount = Number.parseFloat(String(value || '0').replace(/[^0-9.-]/g, ''));
      return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
    }

    productCard(product) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const selected = variants.find(variant => variant.available) || variants[0];
      if (!selected) return '';
      const title = this.cleanTitle(product.title);
      const price = this.priceToCents(selected.price);
      const compareAtPrice = this.priceToCents(selected.compare_at_price);
      const onSale = compareAtPrice > price;
      const rawImage = product.image?.src || product.featured_image?.src || product.images?.[0]?.src || product.images?.[0] || '';
      const image = typeof rawImage === 'string' ? rawImage : '';
      const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',');
      const isHangingCharm = tags.some(tag => /^hanging charms?$/i.test(String(tag).trim()));
      const isDoubleLink = tags.some(tag => /\bdouble links?\b/i.test(String(tag)))
        || /\bdouble links?\b/i.test(String(product.title || ''));
      const hasOptions = variants.length > 1 || String(selected.title || '').toLowerCase() !== 'default title';
      const options = hasOptions ? `<select data-charm-variant aria-label="Choose option for ${this.escape(title)}">${variants.map(variant => {
        const variantPrice = this.priceToCents(variant.price);
        const variantCompare = this.priceToCents(variant.compare_at_price);
        return `<option value="${this.escape(variant.id)}" data-price="${variantPrice}" data-compare-at="${variantCompare}" data-sku="${this.escape(variant.sku || '')}"${String(variant.id) === String(selected.id) ? ' selected' : ''}${variant.available ? '' : ' disabled'}>${this.escape(variant.title || '')} - ${this.escape(this.money(variantPrice))}</option>`;
      }).join('')}</select>` : '';
      const data = {
        id: +selected.id,
        title,
        originalTitle: product.title || '',
        variantTitle: selected.title || '',
        sku: selected.sku || '',
        price,
        compareAtPrice,
        available: Boolean(selected.available),
        image,
        isHangingCharm,
        isDoubleLink
      };
      return `<article class="bb__product${isHangingCharm ? ' is-hanging-charm' : ''}" data-card data-search-text="${this.escape(`${product.title || ''} ${selected.sku || ''}`.toLowerCase())}"><button type="button" class="bb__pick${isHangingCharm ? ' is-hanging-charm' : ''}" data-pick${selected.available ? '' : ' disabled'} aria-label="Select ${this.escape(title)}"><span>${image ? `<img src="${this.escape(image)}" alt="${this.escape(title)}" class="bb__fit-image${isHangingCharm ? ' is-hanging-charm' : ''}" crossorigin="anonymous">` : ''}</span><b>${this.escape(title)}</b></button>${options}<div class="bb__price" data-charm-price><s data-price-compare${onSale ? '' : ' hidden'}>${onSale ? this.escape(this.money(compareAtPrice)) : ''}</s><strong data-price-current${onSale ? ' class="is-sale"' : ''}>${this.escape(this.money(price))}</strong></div><script type="application/json" data-product-json>${JSON.stringify(data).replace(/</g, '\\u003c')}</script></article>`;
    }

    async ensurePanelLoaded(panel) {
      if (!panel?.dataset.productsUrl || panel.dataset.productsLoaded === 'true' || panel.dataset.productsLoading === 'true') return;
      panel.dataset.productsLoading = 'true';
      const status = panel.querySelector('[data-panel-status]');
      if (status) {
        status.hidden = false;
        status.textContent = 'Loading all links…';
      }
      try {
        const products = [];
        let page = 1;
        while (true) {
          const url = new URL(panel.dataset.productsUrl, location.origin);
          url.searchParams.set('limit', '250');
          url.searchParams.set('page', String(page));
          const response = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!response.ok) throw Error('Unable to load this collection.');
          const result = await response.json();
          const batch = Array.isArray(result.products) ? result.products : [];
          products.push(...batch);
          if (batch.length < 250) break;
          page += 1;
        }
        const grid = panel.querySelector('.bb__grid');
        grid.innerHTML = products.length ? products.map(product => this.productCard(product)).join('') : '<p>No products are available in this collection yet.</p>';
        panel.dataset.productsLoaded = 'true';
        this.fitImages(grid.querySelectorAll('.bb__fit-image'));
        this.updateUsedProducts();
        this.search(this.root.querySelector('[data-search]').value);
        if (status) status.hidden = true;
      } catch (error) {
        if (status) status.textContent = 'This collection could not be loaded. Please try the category again.';
      } finally {
        panel.dataset.productsLoading = 'false';
      }
    }

    search(value) {
      const query = value.trim().toLowerCase();
      this.root.querySelectorAll('[data-card]').forEach(card => { card.hidden = Boolean(query) && !card.dataset.searchText.includes(query); });
    }

    status(message, error = false) {
      this.els.status.textContent = message;
      this.els.status.classList.toggle('is-error', error);
    }

    async addToCart() {
      const base = this.variant();
      if (!base?.available) return this.status('This bracelet size is currently unavailable.', true);
      const properties = {
        '_Bracelet build ID': this.buildId,
        'Bracelet finish': this.style.label,
        'Base bracelet length': `${this.baseLength} links`,
        'Finished bracelet length': `${this.length} links`
      };
      const items = [{ id: +base.id, quantity: 1, properties }];
      this.slots.forEach((charm, index) => {
        if (charm && !this.isContinuation(charm)) items.push({
          id: +charm.id,
          quantity: 1,
          properties: {
            '_Bracelet build ID': this.buildId,
            'Bracelet position': charm.isDoubleLink ? `Links ${index + 1}–${index + 2} of ${this.length}` : `Link ${index + 1} of ${this.length}`,
            'Bracelet finish': this.style.label
          }
        });
      });
      this.els.adds.forEach(button => { button.disabled = true; button.classList.add('is-loading'); });
      this.status('Adding your bracelet…');
      try {
        const response = await fetch(this.root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items })
        });
        const result = await response.json();
        if (!response.ok) throw Error(result.description || result.message || 'Unable to add this bracelet.');
        location.assign(this.root.dataset.cartUrl);
      } catch (error) {
        this.status(error.message || 'Something went wrong. Please try again.', true);
        this.els.adds.forEach(button => { button.disabled = false; button.classList.remove('is-loading'); });
      }
    }

    escape(value) {
      const div = document.createElement('div');
      div.textContent = String(value || '');
      return div.innerHTML.replace(/"/g, '&quot;');
    }
  }

  const init = (scope = document) => scope.querySelectorAll('.bb[data-section-id]').forEach(root => {
    if (!root.dataset.initialized) {
      root.dataset.initialized = 'true';
      new BraceletBuilder(root);
    }
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
